import {SourceFile} from './source-file';
import {SourceFolder} from './source-folder';

/**
 * Path-safety core shared by VCC source files and agent instructions.
 *
 * Lifted out of `isAgentInstructionFilePath` so both domains enforce one
 * definition of "safe relative path". Deliberately NOT tightened beyond what
 * that function already did: every rule here also applies to existing
 * instruction paths, and `SourceFileRepository` re-parses stored rows on every
 * read, so a stricter core would make already-persisted documents unreadable
 * (a 500 on hydration rather than a rejected write). VCC-specific rules layer
 * on top in `validateSourceFileWrite` instead.
 *
 * Rejects: leading/trailing whitespace, backslashes, NUL, absolute paths, and
 * empty (`//`), `.`, or `..` segments. Filename and folder segments must
 * satisfy the schemas that own them.
 */
export const isSafeRelativeFilePath = (path: string): boolean => {
  if (path.trim() !== path || path.includes('\\') || path.includes('\0')) {
    return false;
  }

  const segments = path.split('/');
  if (
    path.startsWith('/') ||
    segments.some(
      (segment) => segment === '' || segment === '.' || segment === '..'
    )
  ) {
    return false;
  }

  const filename = segments[segments.length - 1];
  return (
    filename !== undefined &&
    SourceFile.shape.name.safeParse(filename).success &&
    segments
      .slice(0, -1)
      .every((segment) => SourceFolder.shape.name.safeParse(segment).success)
  );
};

/**
 * Extensions a client may write. Intentionally small — widen on demand rather
 * than guessing at what the sandbox toolchain can handle. `.d.ts` is covered by
 * `.ts`.
 */
export const SOURCE_FILE_WRITE_EXTENSIONS = [
  '.ts',
  '.tsx',
  '.js',
  '.jsx',
  '.css',
  '.json',
  '.md',
] as const;

/** Per-file source cap. Sized to be far above any hand-written module. */
export const SOURCE_FILE_MAX_CONTENT_BYTES = 256 * 1024;

/**
 * Absolute server-side ceiling. This stays above the normal editor limit so
 * build artifacts can be synchronized, while preventing a client from using
 * the multiplayer update path to persist an unbounded blob.
 */
export const SOURCE_FILE_HARD_MAX_CONTENT_BYTES = 4 * 1024 * 1024;

/**
 * Longest leading block comment a Code Function may carry as its catalog
 * description. The catalog rides along with every AI Assistant message, so
 * this keeps each entry close to one line. Unrelated to the stored
 * `SourceFile.description` limit.
 */
export const SITE_FUNCTION_DESCRIPTION_MAX_LENGTH = 200;

// No control character belongs in a path. Includes \t, \n and \r: the
// whitespace check only catches those at the ends, not in the middle.
// eslint-disable-next-line no-control-regex
const PATH_CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

/**
 * Why a write was refused. Kept as a closed union so every caller — socket
 * error, tool error, UI toast — maps the same reason to its own wording.
 */
export type SourceFileWriteViolation =
  | 'unsafe-path'
  | 'control-characters'
  | 'unsupported-extension'
  | 'content-too-large'
  | 'content-exceeds-hard-limit';

const exceedsByteCap = (content: string, cap: number): boolean => {
  // UTF-8 spends at most 3 bytes per UTF-16 code unit, so anything under a
  // third of the cap can't exceed it and never needs encoding.
  if (content.length * 3 <= cap) {
    return false;
  }
  return new TextEncoder().encode(content).length > cap;
};

/** Path-shaped violations, split out so a row's `path` gets the same rules. */
export type RelativePathViolation = Extract<
  SourceFileWriteViolation,
  'unsafe-path' | 'control-characters'
>;

/**
 * The path-shaped rules on their own. A source file carries two paths — `name`
 * and the optional `path` that the projector actually writes to — and both have
 * to clear these before either is trusted.
 */
export const validateRelativePath = (
  path: string
): RelativePathViolation | null => {
  if (PATH_CONTROL_CHARS.test(path)) {
    return 'control-characters';
  }
  if (!isSafeRelativeFilePath(path)) {
    return 'unsafe-path';
  }
  return null;
};

/**
 * Validates a source-file write. Returns the first violation, or null when the
 * file is acceptable.
 *
 * Path-shaped rules only — reserved projected paths (`/index.ts`,
 * `/package.json`, …) are checked one layer up, in
 * `@packages/systems/code-libraries/core`, which owns those constants.
 */
export const validateSourceFilePathAndContent = (input: {
  name: string;
  content?: string;
}): SourceFileWriteViolation | null => {
  const pathViolation = validateRelativePath(input.name);
  if (pathViolation) {
    return pathViolation;
  }
  if (
    input.content !== undefined &&
    exceedsByteCap(input.content, SOURCE_FILE_HARD_MAX_CONTENT_BYTES)
  ) {
    return 'content-exceeds-hard-limit';
  }
  if (
    !SOURCE_FILE_WRITE_EXTENSIONS.some((extension) =>
      input.name.endsWith(extension)
    )
  ) {
    return 'unsupported-extension';
  }
  if (
    input.content !== undefined &&
    exceedsByteCap(input.content, SOURCE_FILE_MAX_CONTENT_BYTES)
  ) {
    return 'content-too-large';
  }
  return null;
};
